import React, { useContext, useEffect, useState } from "react";
import { withPrismicUnpublishedPreview } from "gatsby-plugin-prismic-previews";
import styled from "styled-components";
import { Link, graphql } from "gatsby";
import { use100vh } from "react-div-100vh";
import { useAsyncFn } from "react-use";

// Context
import { PageSetup } from "../components/context/page-context";

// Utils
import { formatPrice } from "../components/utils/format-price";

// SEO
import { PageSeo } from "../components/seo/page-seo";

const Page = styled.div`
  padding: 51px 0 0 0;
  min-height: calc(${props => props.height}px - 50px - 50px);

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  color: #0b646f;

  & .page-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 50px;

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }

  & h1,
  & h2,
  & p {
    font-size: 22px;
    line-height: 32px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  & h1 {
    font-size: 32px;
    margin-bottom: 30px;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  & h2 {
    font-size: 24px;
    margin: 30px 0 20px 0;
    text-align: left;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  & .order-info {
    background: #f5eadf;
    padding: 30px;
    margin: 30px 0;
    border-radius: 8px;

    & .order-number {
      text-align: left;
      margin: 20px 0;
    }

    & .order-status {
      font-size: 24px;

      color: #0b646f;
      text-align: left;
      margin: 20px 0;
    }
  }

  & .order-details {
    text-align: left;
    margin: 30px 0;

    & .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid #f5eadf;

      & .label {
      }

      & .value {
      }
    }
  }

  & .tracking-info {
    background: #fff;
    border: 2px solid #f5eadf;
    padding: 20px;
    margin: 20px 0;
    border-radius: 8px;

    & .tracking-number {
      font-family: monospace;
      font-size: 18px;

      text-align: center;
      margin: 10px 0;
    }
  }

  & .back-link {
    text-align: center;
    margin-top: 40px;

    & a {
      color: #debb9b;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  & .contact-info {
    background: #f5eadf;
    padding: 20px;
    margin: 30px 0;
    border-radius: 8px;
    text-align: center;

    & p {
      margin: 10px 0;
    }
  }
`;

const OrderTracking = ({ location, data }) => {
  const [pageSetup, setPageSetup] = useContext(PageSetup);
  const height = use100vh();
  const [orderData, setOrderData] = useState(null);

  const [fetchState, fetchOrder] = useAsyncFn(
    async orderId => {
      // First try the token-specific function for alphanumeric tokens
      if (orderId.length > 20 && /^[a-f0-9]+$/i.test(orderId)) {
        try {
          const tokenResponse = await fetch(
            `/.netlify/functions/get-order-by-token`,
            {
              method: "POST",
              body: JSON.stringify({
                orderToken: orderId,
              }),
              headers: new Headers({
                "Content-Type": "application/json",
              }),
            }
          );

          if (tokenResponse.ok) {
            const result = await tokenResponse.json();
            setOrderData(result);
            return result;
          }
        } catch (error) {
          console.log(
            "Token lookup failed, trying standard lookup:",
            error.message
          );
        }
      }

      // Fallback to standard order lookup
      const response = await fetch(`/.netlify/functions/get-order-by-number`, {
        method: "POST",
        body: JSON.stringify({
          orderNumber: orderId,
        }),
        headers: new Headers({
          "Content-Type": "application/json",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch order");
      }

      const result = await response.json();
      setOrderData(result);
      return result;
    },
    [setOrderData]
  );

  useEffect(() => {
    setPageSetup({
      pageTitle: `Order Tracking`,
      pageColor: `#DDE1D4`,
      pageType: `not-found`,
      filterArtists: [],
      selectedArtists: [],
      sortOption: null,
      priceOption: null,
      artistOption: [],
      applyFilter: false,
    });
  }, []);

  useEffect(() => {
    // Extract order ID from URL
    const pathParts = location.pathname.split("/");

    // Handle different URL patterns:
    // /orders/{orderId}/authenticate -> orderId is second-to-last
    // /order-tracking/{orderId} -> orderId is last
    let orderId;

    if (
      pathParts.includes("orders") &&
      pathParts[pathParts.length - 1] === "authenticate"
    ) {
      // URL pattern: /orders/{orderId}/authenticate
      orderId = pathParts[pathParts.length - 2];
    } else {
      // URL pattern: /order-tracking/{orderId} or similar
      orderId = pathParts[pathParts.length - 1];
    }

    if (orderId && orderId !== "authenticate") {
      fetchOrder(orderId);
    }
  }, [location.pathname, fetchOrder]);

  if (fetchState.loading) {
    return (
      <Page height={height}>
        <div className="page-container">
          <h1>Loading Order Details...</h1>
        </div>
      </Page>
    );
  }

  if (fetchState.error || !orderData) {
    return (
      <>
        <PageSeo seoTitle={`Order Not Found`} seoText={null} seoImage={null} />
        <Page height={height}>
          <div className="page-container">
            <h1>Order Not Found</h1>
            <p>The order you're looking for could not be found.</p>
            <div className="back-link">
              <Link to={`/shop/`}>Continue Shopping</Link>
            </div>
          </div>
        </Page>
      </>
    );
  }

  return (
    <>
      <PageSeo
        seoTitle={`Order Tracking - ${orderData.number}`}
        seoText={`Track your House of Voltaire order`}
        seoImage={null}
      />
      <Page height={height}>
        <div className="page-container">
          <h1>Order Tracking</h1>

          <div className="order-info">
            <h2>Order Details</h2>
            <div className="order-number">Order Number:{orderData.name}</div>
            <div className="order-status">
              Financial: {orderData.financial_status} | Fulfillment:{" "}
              {orderData.fulfillment_status || "Unfulfilled"}
            </div>
          </div>

          <div className="order-details">
            <h2>Order Information</h2>
            <div className="detail-row">
              <span className="label">Order Date:</span>
              <span className="value">
                {new Date(orderData.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Total:</span>
              <span className="value">
                {formatPrice(orderData.currency, orderData.total_price)}
              </span>
            </div>
            {orderData.shipping_address && (
              <div className="detail-row">
                <span className="label">Shipping Address:</span>
                <span className="value">
                  {orderData.shipping_address.address1},{" "}
                  {orderData.shipping_address.city},{" "}
                  {orderData.shipping_address.country}
                </span>
              </div>
            )}
          </div>

          {orderData.tracking_numbers && orderData.tracking_numbers.length > 0 && (
            <div className="tracking-info">
              <h2>Tracking Information</h2>
              {orderData.tracking_numbers.map((trackingNumber, index) => (
                <div key={index} className="tracking-number">
                  {trackingNumber}
                </div>
              ))}
              <p>Your order is being processed and will be shipped soon.</p>
            </div>
          )}

          <div className="contact-info">
            <h2>Need Help?</h2>
            <p>
              If you have any questions about your order, please contact us:
            </p>
            <p>
              <strong>Email:</strong> sales@studiovoltaire.org
            </p>
            <p>
              <strong>Phone:</strong> +44 (0) 20 1234 5678
            </p>
          </div>

          <div className="back-link">
            <Link to={`/shop/`}>Continue Shopping</Link>
          </div>
        </div>
      </Page>
    </>
  );
};

export default withPrismicUnpublishedPreview(OrderTracking);
